import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import "dayjs/locale/pt-br";
import { pdf } from "@react-pdf/renderer";
import { useEffect, useState } from "react";

import api from "../../services/api";

import { useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import { renderToStaticMarkup } from "react-dom/server";
import PDFLineChartComponent from "../pdfSlaCharts";
import PDFPieChart from "../pdfSlaCharts/pieChart";
import { useTheme } from "@mui/system";
import { PageTemplateEasyReport } from "../generateReport";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);
dayjs.locale("pt-br");

const INVERSED_POWER_RX =
  window?.config?.REACT_APP_INVERSED_POWER_RX ||
  process.env.REACT_APP_INVERSED_POWER_RX;

const RX_POWER_REF = -28;
const AVAILABILITY_REF = 97.5;

export default function RelatorioOnuPdfEmail() {
  // Obter dados da URL
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const dadosString = searchParams.get("dados");
  const dadosDecodificados = JSON.parse(decodeURIComponent(dadosString));
  // States
  const [blobString, setBlobString] = useState(null);
  const [triggered, setTriggered] = useState(false);
  // Estilos
  const theme = useTheme();

  useEffect(() => {
    if (!triggered) {
      handleDados();
      setTriggered(true);
    }
  }, []);

  // Gerar relatório
  const handleDados = async () => {
    try {
      // Obter configurações de cabeçalho
      const config = await api.post("/find/generalSetting", {
        product: "usageReport",
      });
      const logo = config?.data?.imagem
        ? await handleUrlToBase64({ url: config?.data?.imagem })
        : null;
      const generalSettings =
        Object.keys(config?.data)?.length > 0
          ? {
              logo: logo,
              ...config?.data,
            }
          : {};
      // Obter json do relatório gerado
      const report = await api.post("/find/historyReport", {
        id: dadosDecodificados?.id,
      });

      const content = report?.data || {}; // O JSON retornado já é o conteudo formatado
      const relatorioInfo = content?.relatorio || {};
      const reportData = content?.data || [];

      // Garantir compatibilidade com o template
      // O template espera que 'client' seja o nome do cliente (string) em relatorio.client
      const clientName =
        content?.cliente?.razaoSocial || content?.cliente?.nomeFantasia || "";

      const selectPages = {
        teoric: false,
        sla: false,
        onu: true,
      };

      // Gerar gráficos e adicionar aos objetos
      const resultsWithGraphs = await Promise.all(
        reportData?.map(async (item) => {
          const [graphRxPower, graphAvailability] = await Promise.all([
            handleGenerateGraphPNG({
              chart: <WrapAreaChart data={item?.history} />,
              width: 900,
              height: 180,
            }),
            handleGenerateGraphPNG({
              chart: <WrapPieChart data={item?.history} />,
              width: 400,
              height: 260,
            }),
          ]);

          return {
            ...item,
            graphRxPower,
            graphAvailability,
          };
        }),
      );

      const results = resultsWithGraphs.map((item) => ({
        ...item,
        onu: { onuItem: item.label }, // Adaptando para o template esperar onu.onuItem
        availability: parseFloat(item.availability), // Garantir que seja number
        rxPowerMean: parseFloat(item.rxPowerMean), // Garantir que seja number
      }));

      // Criar arquivo PDF
      const MyDoc = (
        <PageTemplateEasyReport
          relatorio={{
            ...relatorioInfo,
            client: clientName,
            source: `Monitoramento ${
              window?.config?.REACT_APP_NAME_ENTERPRISE ||
              process.env.REACT_APP_NAME_ENTERPRISE
            }`,
            createdAt: dayjs().tz("America/Sao_Paulo"),
            dataInicial: content?.initialDate,
            dataFinal: content?.finalDate,
          }}
          onuSla={{
            results,
            rxPowerRef: RX_POWER_REF,
            availabilityRef: AVAILABILITY_REF,
          }}
          enterpriseConfig={{ ...generalSettings }}
          selectPages={selectPages}
        />
      );

      // Gerar o PDF como Blob e converter para base64
      const blob = await pdf(MyDoc).toBlob();
      const blobAsBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64data = reader.result.split(",")[1];
          resolve(base64data);
        };
        reader.readAsDataURL(blob);
      });

      setBlobString(blobAsBase64);
    } catch (error) {
      console.error(error);
    }
  };

  // Gerar Imagems
  async function handleGenerateGraphPNG({ chart, width = 790, height = 200 }) {
    let staticHtmlTraffic = renderToStaticMarkup(chart);
    let graphc = await handleApiGetBase64({
      data: btoa(unescape(encodeURIComponent(staticHtmlTraffic))),
      width: width,
      height: height,
    });
    return graphc;
  }
  async function handleApiGetBase64(htmlBase64) {
    try {
      const response = await api.post("/cliente/relatorio/htmlToImageBase64", {
        htmlBase64,
      });

      if (response.data) {
        return response.data;
      }
    } catch (error) {
      throw error;
    }
  }
  async function handleUrlToBase64({ url, options = {} }) {
    try {
      const response = await api.post("/cliente/relatorio/urlToImageBase64", {
        url,
        options,
      });

      if (response.data) {
        return response?.data;
      }
    } catch (error) {
      console.error("Error: ", error);
      return null;
    }
  }

  // Renderizar Gráficos
  const WrapAreaChart = ({ data, type = 3, refLine = [] }) => (
    <PDFLineChartComponent
      mainColor={theme.palette.cardGraphic.graphColor.green}
      mainLineColor={theme.palette.cardGraphic.graphLineColor.green}
      data={data || []}
      theme={theme}
      areaChartOptions={{
        height: 160,
        width: 900,
      }}
      type={type}
      referenceLines={refLine}
      yOptions={{ domain: INVERSED_POWER_RX ? [0, 40] : [-40, 0] }}
      invertNegativeValues
    />
  );

  const WrapPieChart = ({ data }) => {
    const validSamples = data?.filter(
      (d) => d?.value !== null && d?.value <= 0,
    );
    const total = validSamples?.length;
    const good = validSamples?.filter((d) => d?.value > -28)?.length;
    const availability = (good / total) * 100 || 0;

    return (
      <PDFPieChart
        data={[
          { name: "Disponível", value: availability },
          { name: "Indisponível", value: 100 - availability },
        ]}
        theme={theme}
      />
    );
  };

  return (
    <>
      <Box id="pdf">{blobString && blobString}</Box>
    </>
  );
}
